import { createPopper, type Instance, type Options } from "@popperjs/core";
import type { Action } from "svelte/action";

export type TooltipProps = {
  title: string;
  options?: Partial<Options>;
  showDelayMs?: number;
  /** Where to mount the tooltip; by default right after the element. */
  container?: HTMLElement;
};

export type TooltipParams = string | TooltipProps | false | undefined;

const getPropsFromParam = (param: TooltipParams): TooltipProps =>
  typeof param === "object" ? param : { title: param || "" };

/**
 * A Svelte action that adds Popper.js tooltips to elements.
 * If tooltip is undefined/falsy, no DOM elements or listeners are created.
 * Tooltip DOM and Popper are initialised on the first show.
 *
 * At some point this can be replaced with an Attachment, but until then,
 * the fromAction helper can be used for compatibility.
 *
 * ```svelte
 * <script lang="ts">
 *   import tooltip from "$lib/design-systems/product/primitives/overlays/tooltip";
 * </script>
 *
 * <button use:tooltip={"Tooltip text"}>
 *   Hover me!
 * </button>
 * ```
 *
 * For more information, see the Popper.js v2 tutorial:
 * https://popper.js.org/docs/v2/tutorial/
 */
const tooltip: Action<HTMLElement, TooltipParams> = (node, param) => {
  let tooltipEl: HTMLDivElement | null = null;
  let textNode: Text | null = null;
  let popperInstance: Instance | null = null;
  let active = false;
  let currentTitle = "";
  let currentOptions: Partial<Options> | undefined;
  let currentShowDelayMs = 0;
  let currentContainer: HTMLElement | undefined;
  let showTimer: ReturnType<typeof setTimeout> | null = null;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  // Matches the fade-out in app.css (.popper-tooltip transition-opacity
  // duration-300); the element leaves the layout once invisible.
  const FADE_OUT_MS = 300;

  const showEvents = ["mouseenter", "focus"];
  const hideEvents = ["mouseleave", "blur"];

  function updatePopperOptions() {
    const providedModifiers = currentOptions?.modifiers ?? [];
    popperInstance?.setOptions({
      // "fixed" so ancestors with overflow: hidden/clip (e.g. scrollable tables) don't cut the tooltip off
      strategy: "fixed",
      ...currentOptions,
      modifiers: [
        { name: "offset", options: { offset: [0, 12] } },
        { name: "eventListeners", enabled: active },
        { name: "arrow", options: { padding: 8 } },
        { name: "preventOverflow", options: { padding: 16 } },
        ...providedModifiers,
      ],
    });
  }

  function clearShowTimer() {
    if (!showTimer) return;
    clearTimeout(showTimer);
    showTimer = null;
  }

  function clearHideTimer() {
    if (!hideTimer) return;
    clearTimeout(hideTimer);
    hideTimer = null;
  }

  function show() {
    clearShowTimer();
    clearHideTimer();
    if (!currentTitle) return;
    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.className = "popper-tooltip";
      textNode = document.createTextNode(currentTitle);
      tooltipEl.appendChild(textNode);

      const arrow = document.createElement("div");
      arrow.className = "popper-arrow";
      arrow.setAttribute("data-popper-arrow", "");
      tooltipEl.appendChild(arrow);

      if (currentContainer) currentContainer.appendChild(tooltipEl);
      else node.parentElement?.insertBefore(tooltipEl, node.nextSibling);
      popperInstance = createPopper(node, tooltipEl);
    }
    active = true;
    updatePopperOptions();
    // A faded-out tooltip still has a box, and when an ancestor establishes a
    // containing block (transform, filter, ...) position:fixed resolves
    // against it, so a tooltip positioned past the end of an inner scroll
    // container extends that container's scrollable area. Keep the element
    // out of layout until it is actually shown.
    tooltipEl.style.display = "block";
    // Force a style recalc while the element is measurable but still
    // opacity-0, so the opacity transition animates in from display:none.
    void tooltipEl.offsetHeight;
    popperInstance?.update();
    tooltipEl.setAttribute("data-show", "");
  }

  function scheduleShow() {
    clearShowTimer();
    if (currentShowDelayMs > 0) {
      showTimer = setTimeout(show, currentShowDelayMs);
      return;
    }
    show();
  }

  function hide() {
    clearShowTimer();
    if (!tooltipEl) return;
    active = false;
    updatePopperOptions();
    tooltipEl.removeAttribute("data-show");
    clearHideTimer();
    hideTimer = setTimeout(() => {
      if (!active && tooltipEl) tooltipEl.style.display = "none";
    }, FADE_OUT_MS);
  }

  function setup({ title, options, showDelayMs = 0, container }: TooltipProps) {
    currentTitle = title;
    currentContainer = container;
    currentOptions = options;
    currentShowDelayMs = showDelayMs;

    showEvents.forEach((event) => node.addEventListener(event, scheduleShow));
    hideEvents.forEach((event) => node.addEventListener(event, hide));
  }

  function teardown() {
    clearShowTimer();
    clearHideTimer();
    showEvents.forEach((event) =>
      node.removeEventListener(event, scheduleShow),
    );
    hideEvents.forEach((event) => node.removeEventListener(event, hide));
    popperInstance?.destroy();
    popperInstance = null;
    tooltipEl?.remove();
    tooltipEl = null;
    textNode = null;
    currentTitle = "";
    active = false;
  }

  const initialProps = getPropsFromParam(param);
  if (initialProps.title) {
    setup(initialProps);
  }

  return {
    update(param) {
      const props = getPropsFromParam(param);

      if (!props.title) {
        teardown();
        return;
      }

      if (!currentTitle) {
        setup(props);
        return;
      }

      let changed = false;
      if (currentTitle !== props.title) {
        currentTitle = props.title;
        if (textNode) textNode.data = props.title;
        changed = true;
      }
      if (currentOptions !== props.options) {
        currentOptions = props.options;
        updatePopperOptions();
        changed = true;
      }
      if (currentShowDelayMs !== (props.showDelayMs ?? 0)) {
        currentShowDelayMs = props.showDelayMs ?? 0;
        changed = true;
      }
      if (changed) {
        popperInstance?.update();
      }
    },

    destroy() {
      teardown();
    },
  };
};

export default tooltip;
